import { useMemo, useState, type ComponentType, type SVGProps } from 'react';
import { Link } from 'react-router-dom';
import {
  IconAnnouncements,
  IconEvents,
  IconHR,
  IconMembers,
  IconFinance,
  IconInvestments,
  IconOperations,
  IconProcurement,
  IconReports,
  IconView,
} from '../../components/icons';
import { useAuth } from '../../auth/useAuth';
import { hasAnyPermission } from '../../utils/roles';

// TS port of the source (Falah) app's pages/reports/ReportsIndex.jsx, with
// its module list trimmed to only the modules this port keeps — Education
// and Campaigns (Programmes/Agents/Vendors/Vouchers) were dropped wholesale
// in earlier phases of this migration, and People's Volunteer Time Log
// report went with the (also dropped) Volunteers module. The remaining
// People reports were renamed to their Memberships equivalents (see
// pages/reports/memberships/*).
//
// Each report's `permissions` mirrors the exact RequireAuth permission gate
// on its route in App.tsx (and, one level down, the `permission:...`
// middleware in routes/api.php) — a report tile is never shown for
// something the backend would 403 for. Omitting `permissions` means the
// route carries no gate at all (Announcements/Events — open to every
// authenticated user, matching routes/api.php). A category disappears
// entirely once none of its reports are visible, rather than showing an
// empty tile grid.
interface ReportLink {
  to: string;
  label: string;
  description: string;
  permissions?: string[];
}

interface ReportCategory {
  title: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  reports: ReportLink[];
}

// Mirrors AppShell's SHOW_RESOURCES_NAV, which is already true — the
// Resources reports were previously left hidden here even though their
// pages/routes/permissions were fully implemented and the nav itself
// already links to the live feature.
const SHOW_RESOURCES_REPORTS = true;

const RESOURCES_CATEGORY: ReportCategory = {
  title: 'Resources',
  icon: IconOperations,
  reports: [
    {
      to: '/reports/resources/asset-register',
      label: 'Asset Register',
      description: 'Full asset listing with category, department, value and vendor.',
      permissions: ['resources.reports.view'],
    },
    {
      to: '/reports/resources/depreciation-schedule',
      label: 'Depreciation Schedule',
      description: 'Cost, accumulated depreciation and net book value for every fixed asset.',
      permissions: ['resources.reports.view'],
    },
    {
      to: '/reports/resources/asset-movement-log',
      label: 'Asset Movement Log',
      description: 'Location, department and custodian changes for every asset.',
      permissions: ['resources.reports.view'],
    },
    {
      to: '/reports/resources/facility-directory',
      label: 'Facility Directory',
      description: 'Facilities with building, floor, capacity and status.',
      permissions: ['resources.reports.view'],
    },
    {
      to: '/reports/resources/stock-levels',
      label: 'Inventory Stock Levels',
      description: 'Current stock levels per item, flagging what is below reorder point.',
      permissions: ['resources.reports.view'],
    },
    {
      to: '/reports/resources/movement-log',
      label: 'Inventory Movement Log',
      description: 'History of inventory received, issued and adjusted.',
      permissions: ['resources.reports.view'],
    },
    {
      to: '/reports/resources/bookings-register',
      label: 'Bookings Register',
      description: 'Every facility booking, with time range and status.',
      permissions: ['resources.reports.view'],
    },
  ],
};

// The exact OR-list RequireAuth uses for /reports/custom in App.tsx —
// holding any single report permission is enough to build a report pack.
const REPORT_PACK_PERMISSIONS = [
  'memberships.reports.view',
  'revenue.reports.view',
  'procurement.reports.view',
  'resources.reports.view',
  'hr.reports.view',
  'investments.reports.view',
  'penalties.reports.view',
  'gl_interface.reports.view',
  'memberships.benefits.reports.view',
  'journals.reports.view',
  'consolidated.reports.view',
  'supplier.reports.view',
  'banking.reports.view',
  'budgets.reports.view',
];

const CATEGORIES: ReportCategory[] = [
  {
    title: 'Custom',
    icon: IconReports,
    reports: [
      {
        to: '/reports/custom',
        label: 'Report Packs',
        description: 'Build and generate a combined PDF from any set of reports — e.g. for a monthly board meeting.',
        permissions: REPORT_PACK_PERMISSIONS,
      },
    ],
  },
  {
    title: 'Consolidated',
    icon: IconFinance,
    reports: [
      {
        to: '/reports/consolidated/trial-balance',
        label: 'Trial Balance',
        description: 'Cumulative debit/credit balance of every account since ledger inception, as at a given date.',
        permissions: ['consolidated.reports.view'],
      },
      {
        to: '/reports/consolidated/income-statement',
        label: 'Income Statement',
        description: 'Revenue less expenses for a date range, yielding net surplus or deficit.',
        permissions: ['consolidated.reports.view'],
      },
      {
        to: '/reports/consolidated/balance-sheet',
        label: 'Balance Sheet',
        description: 'Assets, liabilities and equity as at a given date.',
        permissions: ['consolidated.reports.view'],
      },
      {
        to: '/reports/consolidated/statement-of-changes',
        label: 'Statement of Changes in Fund Balance',
        description: 'Opening fund balance plus net surplus/deficit for the period equals closing fund balance.',
        permissions: ['consolidated.reports.view'],
      },
      {
        to: '/reports/consolidated/cash-flow',
        label: 'Statement of Cash Flows',
        description: 'Net surplus adjusted for depreciation, disposal gain/loss, and working-capital movement.',
        permissions: ['consolidated.reports.view'],
      },
      {
        to: '/reports/consolidated/comprehensive-income',
        label: 'Statement of Comprehensive Income',
        description: 'Net Surplus/(Deficit) plus Other Comprehensive Income (asset revaluation surplus movements).',
        permissions: ['consolidated.reports.view'],
      },
      {
        to: '/reports/consolidated/ratio-analysis',
        label: 'Ratio Analysis',
        description: 'Standard liquidity, solvency and profitability ratios computed from the Chart of Accounts.',
        permissions: ['consolidated.reports.view'],
      },
    ],
  },
  {
    title: 'Announcements',
    icon: IconAnnouncements,
    // Not permission-gated — open to every authenticated user, matching
    // their unguarded routes in App.tsx/routes/api.php.
    reports: [
      {
        to: '/reports/announcements/log',
        label: 'Announcement Log',
        description: 'Every announcement published, with priority, dates and author.',
      },
      {
        to: '/reports/announcements/active',
        label: 'Active Announcements',
        description: 'Announcements currently live (published and not yet expired).',
      },
    ],
  },
  {
    title: 'Benefit Payments',
    icon: IconMembers,
    reports: [
      {
        to: '/reports/benefits/claims-by-stage',
        label: 'Benefit Claims in Progress by Stage',
        description: 'Every benefit claim not yet disbursed, reversed, or closed, grouped by its current stage.',
        permissions: ['memberships.benefits.reports.view'],
      },
      {
        to: '/reports/benefits/claims-register',
        label: 'Benefit Claims Register',
        description: 'Every benefit claim ever captured, at any stage — including disbursed, reversed, and rejected.',
        permissions: ['memberships.benefits.reports.view'],
      },
      {
        to: '/reports/benefits/payment-batches',
        label: 'Benefit Payment Batches Register',
        description: 'Every EFT batch line generated for a benefit claim, with bank details and disbursement status.',
        permissions: ['memberships.benefits.reports.view'],
      },
      {
        to: '/reports/benefits/cheque-register',
        label: 'Benefit Cheque Register',
        description: 'Every cheque issued against a benefit claim, with signature progress and status.',
        permissions: ['memberships.benefits.reports.view'],
      },
    ],
  },
  {
    title: 'Events',
    icon: IconEvents,
    // Not permission-gated — same as Announcements above.
    reports: [
      {
        to: '/reports/events/calendar',
        label: 'Event Calendar',
        description: 'Full event schedule.',
      },
    ],
  },
  {
    title: 'General Ledger Interface',
    icon: IconFinance,
    reports: [
      {
        to: '/reports/gl/interface-log',
        label: 'GL Daily Interface Log',
        description: 'Every automated GL batch generated, with transaction count, total value and posting status.',
        permissions: ['gl_interface.reports.view'],
      },
      {
        to: '/reports/gl/exceptions',
        label: 'GL Exceptions — Rejected Postings',
        description: 'Batch lines FAMS rejected, still outstanding, bucketed by age.',
        permissions: ['gl_interface.reports.view'],
      },
      {
        to: '/reports/gl/reconciliation',
        label: 'Monthly Sub-ledger to GL Reconciliation',
        description: 'Sub-ledger totals compared against what has actually posted to GL, by transaction type.',
        permissions: ['gl_interface.reports.view'],
      },
      {
        to: '/reports/gl/account-mapping',
        label: 'GL Account Mapping',
        description: 'Which chart-of-accounts code each automated transaction type posts against.',
        permissions: ['gl_interface.reports.view'],
      },
      {
        to: '/reports/gl/account-ledger',
        label: 'General Ledger',
        description: 'Every posted transaction for one account, in date order with a running balance.',
        permissions: ['gl_interface.reports.view'],
      },
      {
        to: '/reports/gl/correcting-journals',
        label: 'Correcting Journals Register',
        description: 'Every manual correcting journal entry, with debit/credit totals and posting status.',
        // A different permission from the rest of this category — mirrors
        // App.tsx, where /reports/gl/correcting-journals sits in its own
        // journals.reports.view-gated <Route> block, separate from the
        // other four GL reports' gl_interface.reports.view block.
        permissions: ['journals.reports.view'],
      },
    ],
  },
  {
    title: 'Banking',
    icon: IconFinance,
    reports: [
      {
        to: '/reports/banking/reconciliation',
        label: 'Bank Reconciliation',
        description: 'Balance per the General Ledger vs. cleared statement items, with every unmatched or uncleared line listed.',
        permissions: ['banking.reports.view'],
      },
    ],
  },
  {
    title: 'Budgets',
    icon: IconFinance,
    reports: [
      {
        to: '/reports/budgets/committed',
        label: 'Committed Budget',
        description: 'Each budget line with its revised budget, committed obligations and remaining available balance.',
        permissions: ['budgets.reports.view'],
      },
      {
        to: '/reports/budgets/variance',
        label: 'Budget Variance',
        description: 'Each budget line with its revised budget, actual spend, and variance amount/percent.',
        permissions: ['budgets.reports.view'],
      },
    ],
  },
  {
    title: 'Human Resources',
    icon: IconHR,
    reports: [
      {
        to: '/reports/hr/attendance',
        label: 'Attendance Register',
        description: 'Clock in/out entries with computed duration.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/leave',
        label: 'Leave Register',
        description: 'Every leave request with type, dates and decision status.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/leave-balances',
        label: 'Leave Balances',
        description: 'Entitled, used and remaining leave days per employee and type for the current year.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/payroll',
        label: 'Payroll Register',
        description: 'Every payroll entry with base salary, additions, deductions and net pay.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/overtime',
        label: 'Overtime Report',
        description: 'Overtime requests submitted, with hours and decision status.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/recruitment',
        label: 'Recruitment Pipeline',
        description: 'Every candidate across every vacancy, with pipeline stage and offer details.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/performance',
        label: 'Performance Reviews Register',
        description: 'Every performance review with rating and finalization status.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/training',
        label: 'Training Records Register',
        description: 'Every training record with completion status and certificate presence.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/documents',
        label: 'Employee Documents Register',
        description: "Every document held on an employee's file, by type.",
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/separation',
        label: 'Separation Register',
        description: 'Every separation record with type, effective date and final pay.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/employees',
        label: 'Employee Register',
        description: 'Full employee listing with contact details, current position and manager.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/positions',
        label: 'Positions / Establishment Register',
        description: 'Every position in the establishment, with pay grade, work shift and how many employees currently hold it.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/turnover',
        label: 'Turnover / Attrition',
        description: 'Monthly headcount and finalized separations, with the resulting turnover rate.',
        permissions: ['hr.reports.view'],
      },
      {
        to: '/reports/hr/contract-expiry',
        label: 'Contract Expiry',
        description: 'Fixed-term contract employees whose current position has a recorded end date, soonest first.',
        permissions: ['hr.reports.view'],
      },
    ],
  },
  {
    title: 'Investment Management',
    icon: IconInvestments,
    reports: [
      {
        to: '/reports/investments/register',
        label: 'Investment Register',
        description: 'Full investment listing across every asset class, with amount, dates and status.',
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/allocation',
        label: 'Asset Allocation',
        description: "Current portfolio allocation by asset class, checked against each class's policy range.",
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/income-vs-expected',
        label: 'Income vs Expected',
        description: 'Investment income not yet received, bucketed by days overdue.',
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/change-log',
        label: 'Investment Change Log',
        description: 'Every logged field change across all investments, newest first.',
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/loan-portfolio',
        label: 'Loan Portfolio & Amortisation',
        description: 'Every loan holding with outstanding principal, next installment due, effective yield, security coverage and NPL status.',
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/fixed-interest-maturity',
        label: 'Fixed Interest Maturity Profile',
        description: 'Every bond/fixed-interest holding with face value, coupon rate, yield to maturity, duration, and a forward-looking maturity bucket.',
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/property-lease-register',
        label: 'Property Lease & Expiry Register',
        description: 'Every property holding with lease dates, insurance expiry, a forward-looking expiry bucket, gross rental yield, and occupancy status.',
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/cash-liquidity',
        label: 'Cash & Liquidity',
        description: "Every Cash & Term Deposit holding with accrued interest, yield, and an idle-cash flag, plus the portfolio's overall liquidity ratio.",
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/equity-concentration',
        label: 'Equity Holdings & Concentration',
        description: 'Every equity holding with NAV per unit, unrealised gain/loss, dividend yield, total return, and % of the total equity book — flagged over 25% concentration.',
        permissions: ['investments.reports.view'],
      },
      {
        to: '/reports/investments/offshore-exposure',
        label: 'Offshore Exposure',
        description: 'Every offshore holding with country, currency, local and VUV-translated amount, FX movement since acquisition, and country concentration flagged over 25%.',
        permissions: ['investments.reports.view'],
      },
    ],
  },
  {
    title: 'Memberships',
    icon: IconMembers,
    reports: [
      {
        to: '/reports/memberships/member-directory',
        label: 'Member Directory',
        description: 'Full member listing with contact details and type.',
        permissions: ['memberships.reports.view'],
      },
      {
        to: '/reports/memberships/new-members',
        label: 'New Members Report',
        description: 'Members who joined within a date range.',
        permissions: ['memberships.reports.view'],
      },
      {
        to: '/reports/memberships/member-demographics',
        label: 'Member Demographics',
        description: 'Member breakdown by age band and gender.',
        permissions: ['memberships.reports.view'],
      },
      {
        to: '/reports/memberships/employer-register',
        label: 'Employer Register',
        description: 'Full employer listing with registration, contact and contribution details.',
        permissions: ['memberships.reports.view'],
      },
      {
        to: '/reports/memberships/contribution-schedules',
        label: 'Contribution Schedules Register',
        description: 'Every contribution schedule submitted by employers, with amount due, paid and outstanding.',
        permissions: ['memberships.reports.view'],
      },
    ],
  },
  {
    title: 'Penalties & Surcharges',
    icon: IconMembers,
    reports: [
      {
        to: '/reports/penalties/ageing-schedule',
        label: 'Penalty Ageing Schedule',
        description: 'Outstanding penalties by employer, bucketed by days overdue.',
        permissions: ['penalties.reports.view'],
      },
    ],
  },
  {
    title: 'Procurement',
    icon: IconProcurement,
    reports: [
      {
        to: '/reports/procurement/request-pipeline',
        label: 'Purchase Request Pipeline',
        description: 'Every purchase request with approval status and final outcome.',
        // Not supplier-scopable — a request has no vendor/supplier concept
        // until it becomes a purchase order (see routes/api.php).
        permissions: ['procurement.reports.view'],
      },
      {
        to: '/reports/procurement/order-register',
        label: 'Purchase Order Register',
        description: 'Purchase orders issued, with vendor, amount and delivery date.',
        permissions: ['procurement.reports.view', 'supplier.reports.view'],
      },
      {
        to: '/reports/procurement/receiving-log',
        label: 'Goods Receiving Log',
        description: 'Goods receipts logged against purchase orders.',
        permissions: ['procurement.reports.view', 'supplier.reports.view'],
      },
      {
        to: '/reports/procurement/vendor-payments',
        label: 'Vendor Payments',
        description: 'Payment vouchers and their settlement status per vendor invoice.',
        permissions: ['procurement.reports.view', 'supplier.reports.view'],
      },
      {
        to: '/reports/procurement/ap-ageing',
        label: 'Accounts Payable Ageing',
        description: 'Outstanding vendor invoices bucketed by days overdue.',
        permissions: ['procurement.reports.view', 'supplier.reports.view'],
      },
      {
        to: '/reports/procurement/supplier-register',
        label: 'Supplier Register',
        description: 'Full supplier listing with contact details and purchase order/invoice counts.',
        // Full cross-supplier listing — no self-scoping possible, unlike
        // the other Procurement reports above (see routes/api.php).
        permissions: ['procurement.reports.view'],
      },
      {
        to: '/reports/procurement/spend-analysis',
        label: 'Spend Analysis',
        description: 'Vendor invoices grouped by vendor, with each vendor\'s share of total spend.',
        permissions: ['procurement.reports.view'],
      },
      {
        to: '/reports/procurement/vendor-performance',
        label: 'Vendor Performance',
        description: 'On-time delivery and invoice discrepancy rate per vendor.',
        permissions: ['procurement.reports.view'],
      },
    ],
  },
  {
    title: 'Revenue',
    icon: IconFinance,
    reports: [
      {
        to: '/reports/receipts',
        label: 'Receipts',
        description: 'Export recorded receipts as a bank statement-style CSV for a date range.',
        // A different permission from the rest of this category — mirrors
        // App.tsx, where /reports/receipts shares its gate with the
        // Invoices/Receipts viewing pages (revenue.view|customer.profile.view)
        // rather than the other Revenue reports' revenue.reports.view.
        permissions: ['revenue.view', 'customer.profile.view'],
      },
      {
        to: '/reports/revenue/quotation-register',
        label: 'Quotation Register',
        description: 'All customer quotations with amount, status and validity.',
        permissions: ['revenue.reports.view'],
      },
      {
        to: '/reports/revenue/invoice-register',
        label: 'Invoice Register & Aging',
        description: 'Customer invoices with amount, paid, and outstanding balance.',
        permissions: ['revenue.reports.view'],
      },
      {
        to: '/reports/revenue/ar-ageing',
        label: 'Accounts Receivable Ageing',
        description: 'Outstanding customer invoices bucketed by days overdue.',
        permissions: ['revenue.reports.view'],
      },
      {
        to: '/reports/revenue/receipts-summary',
        label: 'Receipts & Collections Summary',
        description: 'All receipts collected, with payer, method and category.',
        permissions: ['revenue.reports.view'],
      },
      {
        to: '/reports/revenue/by-category',
        label: 'Revenue by Category',
        description: 'Collected revenue totals grouped by type and category.',
        permissions: ['revenue.reports.view'],
      },
      {
        to: '/reports/revenue/customer-register',
        label: 'Customer Register',
        description: 'Full customer listing with contact details and quotation/invoice counts.',
        permissions: ['revenue.reports.view'],
      },
    ],
  },
];

const SORTED_CATEGORIES = [...CATEGORIES, ...(SHOW_RESOURCES_REPORTS ? [RESOURCES_CATEGORY] : [])].sort((a, b) =>
  a.title.localeCompare(b.title)
);

export function ReportsIndex() {
  const { user } = useAuth();
  const [module, setModule] = useState('');

  const visibleCategories = useMemo(
    () =>
      SORTED_CATEGORIES.map((category) => ({
        ...category,
        reports: category.reports.filter((r) => !r.permissions || hasAnyPermission(user, r.permissions)),
      })).filter((category) => category.reports.length > 0),
    [user]
  );

  const shownCategories = module ? visibleCategories.filter((c) => c.title === module) : visibleCategories;

  return (
    <>
      <header className="page-header">
        <h1>Reports</h1>
      </header>
      <p className="muted">Pick a module, then a report.</p>

      <div className="toolbar">
        <select value={module} onChange={(e) => setModule(e.target.value)}>
          <option value="">All modules</option>
          {visibleCategories.map((c) => (
            <option key={c.title} value={c.title}>
              {c.title}
            </option>
          ))}
        </select>
      </div>

      {shownCategories.map((category) => {
        const CategoryIcon = category.icon;
        return (
          <section key={category.title} className="report-category">
            <h2 className="report-category-title">
              <CategoryIcon className="nav-icon" />
              {category.title}
            </h2>
            <div className="report-tile-grid">
              {category.reports.map((report) => (
                <Link key={report.to} to={report.to} className="report-tile">
                  <div className="report-tile-header">
                    <span>{report.label}</span>
                    <IconView className="report-tile-arrow" />
                  </div>
                  <p className="muted">{report.description}</p>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}
